import type { FC } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";

import { SWReactIcons } from "@similarweb/icons";
import { PlainTooltip, Button } from "@similarweb/ui-components";

import { keywordGroupsQueryClient } from "app/services/keyword-groups";
import type { IRecommendation } from "app/common/keyword-research/keyword-generator/types";
import { allTrackers } from "app/services/track/track";
import { CircularLoader } from "app/components/React/CircularLoader/CircularLoader";
import I18n from "pro-features/components/WithTranslation/src/I18n";
import { translate } from "app/filters/i18nFilter";
import { RecommendationsEmptyState } from "pro-features/pages/workspace/common components/RecommendationsSidebar/RecommendationsEmptyState";
import {
    RecommendationsSidebarTopSection,
    RecommendationsSidebarHeader,
    RecommendationsSidebarTitleWrapper,
    RecommendationsSidebarTitle,
    RecommendationsSidebarInfo,
    RecommendationsSidebarSubtitle,
    Separator,
    RecommendationsSidebarContentWrapperKeywords,
    RecommendationKeywordTilesContainer,
    RecommendationsEmptyStateImageAndText,
    RecommendationsSidebarEmptyStateBoldTitle,
    RecommendationsSidebarEmptyStateTitle,
    RecommendationsSidebarEmptyStateWrapper,
    RecommendationsSidebarEmptyStateWrapperKeywords,
} from "pro-features/pages/workspace/common components/RecommendationsSidebar/StyledComponents";
import type { IIsOpen } from "pro-features/pages/workspace/common components/RightBar/src/RightBar";
import { RightBar } from "pro-features/pages/workspace/common components/RightBar/src/RightBar";
import { RecommendationKeywordTile } from "pro-features/pages/workspace/common components/RecommendationsSidebar/RecommendationKeywordTile";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import { useMonitorKeywordsNavigationTargets } from "../../monitorKeywordsNavigationTargets";

const Info = () => (
    <PlainTooltip tooltipContent={translate("workspace.marketing.keywordgroup.recommendation_sidebar.title.tooltip")}>
        <RecommendationsSidebarInfo>
            <SWReactIcons
                iconName="info"
                size="xs"
            />
        </RecommendationsSidebarInfo>
    </PlainTooltip>
);

interface IRecommendationsSidebar {
    isRecommendationOpen: boolean;
    isRecommendationLoading: boolean;
    recommendations: IRecommendation[];
    isSavingAllRecommendations: boolean;
    singleRecommendationLoadingIndex: number | null;
    onAddKeywordClick: (keywords: string[], index?: number) => () => void;
    onClickRecommended: () => void;
    keywordGroupId: string;
}

export const RecommendationsSidebar: FC<IRecommendationsSidebar> = ({
    isRecommendationOpen,
    isRecommendationLoading,
    recommendations,
    isSavingAllRecommendations,
    singleRecommendationLoadingIndex,
    onAddKeywordClick,
    onClickRecommended,
    keywordGroupId,
}) => {
    const { country, isWWW, duration } = useCurrentStateAndParams().params;
    const navTargets = useMonitorKeywordsNavigationTargets();

    const generateKeywordLink = (keyword) => {
        if (!navTargets.keywordOverviewState) {
            return;
        }
        return proNavigator.getStateUrl(navTargets.keywordOverviewState, {
            isWWW,
            country,
            duration,
            keyword,
        });
    };

    const renderPrimaryContent = () => (
        <>
            <RecommendationsSidebarTopSection>
                <RecommendationsSidebarHeader>
                    <RecommendationsSidebarTitleWrapper>
                        <RecommendationsSidebarTitle>
                            {translate("workspace.marketing.keywordgroup.recommendation_sidebar.title")}
                        </RecommendationsSidebarTitle>
                        <Info />
                    </RecommendationsSidebarTitleWrapper>
                </RecommendationsSidebarHeader>
                <RecommendationsSidebarSubtitle>
                    {}
                    <I18n
                        dangerouslySetInnerHTML={true}
                        dataObj={{
                            link: "https://support.similarweb.com/hc/en-us/articles/360011016598#keyword-recommendations",
                        }}
                    >
                        workspace.marketing.keywordgroup.recommendation_sidebar.subtitle
                    </I18n>
                </RecommendationsSidebarSubtitle>
            </RecommendationsSidebarTopSection>
            <Separator />
            <RecommendationsSidebarContentWrapperKeywords>
                {isRecommendationLoading ? (
                    <RecommendationsSidebarEmptyStateWrapper>
                        <CircularLoader
                            options={{
                                svg: {
                                    stroke: "#dedede",
                                    strokeWidth: "4",
                                    r: 21,
                                    cx: "50%",
                                    cy: "50%",
                                },
                                style: {
                                    width: 46,
                                    height: 46,
                                },
                            }}
                        />
                        <RecommendationsSidebarEmptyStateTitle>
                            {translate("workspace.marketing.keywordgroup.recommendation_sidebar.loading_state")}
                        </RecommendationsSidebarEmptyStateTitle>
                    </RecommendationsSidebarEmptyStateWrapper>
                ) : recommendations?.length > 0 ? (
                    <>
                        <Button
                            type={isSavingAllRecommendations ? "primary" : "flat"}
                            isLoading={isSavingAllRecommendations}
                            isDisabled={isSavingAllRecommendations}
                            onClick={onAddKeywordClick(recommendations?.map((x) => x.keyword))}
                        >
                            {isSavingAllRecommendations
                                ? translate(
                                      "workspace.marketing.keywordgroup.recommendation_sidebar.addall.adding.button",
                                  )
                                : translate("workspace.marketing.keywordgroup.recommendation_sidebar.addall.button", {
                                      keywords: recommendations?.length,
                                  })}
                        </Button>
                        <RecommendationKeywordTilesContainer>
                            {recommendations?.map(({ keyword, volume, cpc }, index: number) => (
                                <RecommendationKeywordTile
                                    key={index}
                                    keyword={keyword}
                                    volume={volume}
                                    cpc={cpc}
                                    isLoading={singleRecommendationLoadingIndex === index}
                                    link={generateKeywordLink(keyword)}
                                    onClick={() => {
                                        void keywordGroupsQueryClient
                                            .getGroupById(keywordGroupId)
                                            .then((keywordGroup) => {
                                                allTrackers.dispatchTrackEvent(
                                                    "Button",
                                                    "submit-ok",
                                                    `add recommendation/sidebar/${keywordGroup.Name}`,
                                                );
                                            });
                                        onAddKeywordClick([keyword], index)();
                                    }}
                                />
                            ))}
                        </RecommendationKeywordTilesContainer>
                    </>
                ) : (
                    <RecommendationsSidebarEmptyStateWrapperKeywords>
                        <RecommendationsEmptyStateImageAndText>
                            {RecommendationsEmptyState}
                            <RecommendationsSidebarEmptyStateBoldTitle>
                                {translate("workspace.marketing.keywordgroup.recommendation_sidebar.empty_state.title")}
                            </RecommendationsSidebarEmptyStateBoldTitle>
                            <RecommendationsSidebarEmptyStateTitle>
                                {translate("workspace.marketing.keywordgroup.recommendation_sidebar.empty_state")}
                            </RecommendationsSidebarEmptyStateTitle>
                        </RecommendationsEmptyStateImageAndText>
                    </RecommendationsSidebarEmptyStateWrapperKeywords>
                )}
            </RecommendationsSidebarContentWrapperKeywords>
        </>
    );

    return (
        <RightBar
            data-automation="RecommendationsSidebar"
            primary={(_: IIsOpen) => renderPrimaryContent()}
            onCloseSidebar={onClickRecommended}
            isOpen={isRecommendationOpen}
            isPrimaryOpen={isRecommendationOpen}
            isTableLoading={false}
            marginTop={61}
        />
    );
};
